// Staff.js
// Staff annual reviews (appraisals) — LIVE DATA from the staff review engine.
//
// Pick a pharmacy (and cycle year) → grid of that branch's staff annual
// review documents from <tenant_db>.staff_annual_reviews via
// POST /staff_annual_reviews. Click a staff member to open the full review
// popup (narrative, SR insights with polarity, metric blocks) via
// POST /staff_annual_review, with an on-demand recompute button hitting
// POST /staff_annual_review_refresh.
//
// Row colouring (appraisal RAG, see appraisalState):
//   - green : appraised within the last 12 months
//   - amber : not appraised in the last 12 months, but a future date is booked
//   - red   : never appraised / over a year ago with nothing booked, or a
//             booked date has passed without the appraisal being completed
//
// Names respect the "Hide names" admin toggle (nameMask.js); the narrative
// is masked best-effort via maskTextWithNames since the server renders the
// staff name into prose.
import API_BASE from "./apiBase";

import React, { useState, useMemo, useEffect, useCallback } from "react";
import StaffAppraisal from "./StaffAppraisal";
import ReviewPackView from "./ReviewPack";
import ManageStaff from "./manage_staff";
import {
  displayName, maskTextWithNames, useMaskNames, MaskToggleButton
} from "./nameMask";

const API = `${API_BASE}`;

/* ---------------- Helpers ---------------- */

function authHeaders() {
  // Same fallback HeadOffice.js uses — different screens historically wrote
  // the JWT under different keys.
  const token = localStorage.getItem("token") || localStorage.getItem("authToken");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`
  };
}

function fmtDate(d) {
  if (!d) return "—";
  const t = new Date(d);
  if (isNaN(t.getTime())) return "—";
  return t.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

/* v6: dates sourced from the SIMPLE appraisal tool
   (<tenant>.staff_appraisals) are tinted TEAL, so a manager can tell at a
   glance which dates are backed by a full appraisal pack and which come
   from a quick record. The two now live in separate collections
   (staff_appraisals vs staff_appraisals_advanced) and gridInfoCore tags
   every date it returns with lastAppraisalSource / nextBookedSource. */
const SIMPLE_PILL = {
  display: "inline-block", background: "#eff6ff", border: "1px solid #93c5fd",
  color: "#1976d2", borderRadius: 999, padding: "2px 9px", fontWeight: 700, whiteSpace: "nowrap"
};

function fmtNum(n, dp = 2) {
  if (n === null || n === undefined || isNaN(Number(n))) return "—";
  const v = Number(n);
  return Number.isInteger(v) ? String(v) : v.toFixed(dp);
}

function fmtPct(decimal) {
  if (decimal === null || decimal === undefined || isNaN(Number(decimal))) return "—";
  return `${Math.round(Number(decimal) * 100)}%`;
}

const POLARITY_STYLE = {
  positive: { bg: "#dcfce7", border: "#86efac", text: "#166534", label: "Commendation" },
  concern:  { bg: "#fee2e2", border: "#fca5a5", text: "#991b1b", label: "Discussion point" },
  neutral:  { bg: "#f3f4f6", border: "#d1d5db", text: "#374151", label: "Note" }
};

function yearOptions() {
  const y = new Date().getUTCFullYear();
  return [y, y - 1, y - 2];
}

/* ---------------- Metric row (absolute / per shift / cohort / ratio) ---------------- */

function MetricRow({ label, block, valueSuffix = "" }) {
  if (!block) return null;
  return (
    <tr>
      <td style={{ padding: "6px 10px", fontSize: 13, borderBottom: "1px solid #f0f1f3" }}>{label}</td>
      <td style={{ padding: "6px 10px", fontSize: 13, borderBottom: "1px solid #f0f1f3", textAlign: "right", fontWeight: 600 }}>
        {fmtNum(block.value)}{valueSuffix}
      </td>
      <td style={{ padding: "6px 10px", fontSize: 13, borderBottom: "1px solid #f0f1f3", textAlign: "right" }}>{fmtNum(block.perShift)}</td>
      <td style={{ padding: "6px 10px", fontSize: 13, borderBottom: "1px solid #f0f1f3", textAlign: "right" }}>{fmtNum(block.cohortAvg)}</td>
      <td style={{
        padding: "6px 10px", fontSize: 13, borderBottom: "1px solid #f0f1f3", textAlign: "right",
        color: block.ratio !== null && block.ratio >= 1.5 ? "#b91c1c" : (block.ratio !== null && block.ratio <= 0.5 ? "#6b7280" : "#111827")
      }}>
        {block.ratio !== null && block.ratio !== undefined ? `${fmtNum(block.ratio)}×` : "—"}
      </td>
    </tr>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4,
      color: "#6b7280", margin: "18px 0 8px"
    }}>{children}</div>
  );
}

/* ---------------- Full review popup ---------------- */

function ReviewPopup({ pharmacyName, staffId, staffName, reviewYear, masked, onClose }) {
  const currentYear = new Date().getUTCFullYear();
  const [popupYear, setPopupYear] = useState(reviewYear);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isCurrentCycle = popupYear === currentYear;

  // Opening the popup — or switching cycle year — always RECOMPUTES the
  // review for that (staffId, cycle) via the refresh endpoint, which
  // replaceOne-upserts the stored document. The unique (staffId, reviewYear)
  // index guarantees the existing card is updated in place, never duplicated.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setReview(null);
    try {
      const res = await fetch(`${API}/staff_annual_review_refresh`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ pharmacyName, staffId, reviewYear: popupYear })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setReview(data.review || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [pharmacyName, staffId, popupYear]);

  useEffect(() => { load(); }, [load]);


  const nameShown = displayName(staffName, masked);
  const narrative = review
    ? maskTextWithNames(review.narrative, [review.staffName], masked)
    : null;

  // Escape key closes the dialogue
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Print: build a clean standalone document from the loaded review and
  // print it in a hidden iframe (avoids popup blockers and app CSS bleed).
  const printReview = () => {
    if (!review) return;
    const esc = (s) => String(s ?? "—")
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const m = (s) => esc(maskTextWithNames(s, [review.staffName], masked));

    const metricRows = (rows) => rows.filter(r => r && r.block).map(r => `
      <tr>
        <td>${esc(r.label)}</td>
        <td class="num"><b>${esc(fmtNum(r.block.value))}</b></td>
        <td class="num">${esc(fmtNum(r.block.perShift))}</td>
        <td class="num">${esc(fmtNum(r.block.cohortAvg))}</td>
        <td class="num">${r.block.ratio !== null && r.block.ratio !== undefined ? esc(fmtNum(r.block.ratio)) + "×" : "—"}</td>
      </tr>`).join("");

    const insightsHtml = (review.insights || []).length === 0
      ? `<p class="muted">No statistical outliers identified this year.</p>`
      : review.insights.map(ins => {
          const st = POLARITY_STYLE[ins.polarity] || POLARITY_STYLE.neutral;
          return `<div class="insight" style="border-left:4px solid ${st.border};">
            <div class="insight-head">${esc(ins.id)} · ${esc(ins.title)}
              <span class="tag">${esc(st.label)} · severity ${esc(ins.severity)}</span></div>
            <div>${m(ins.detail)}</div>
          </div>`;
        }).join("");

    const cats = (review.nearMisses?.asDispenserCategories || []);
    const att = review.attendance || {};
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>Annual review — ${esc(displayName(review.staffName, masked))} — ${esc(review.reviewYear)}</title>
      <style>
        body { font-family: Segoe UI, Arial, sans-serif; color: #1f2937; margin: 28px; font-size: 13px; line-height: 1.55; }
        h1 { font-size: 19px; margin: 0 0 2px; }
        .sub { color: #6b7280; font-size: 12px; margin-bottom: 18px; }
        h2 { font-size: 12px; text-transform: uppercase; letter-spacing: 0.4px; color: #6b7280; margin: 20px 0 6px; border-bottom: 1px solid #e5e7eb; padding-bottom: 3px; }
        .narrative { white-space: pre-wrap; background: #f9fafb; border: 1px solid #eef0f3; border-radius: 6px; padding: 10px 12px; }
        table { width: 100%; border-collapse: collapse; margin-top: 4px; }
        th { text-align: right; font-size: 10px; text-transform: uppercase; color: #6b7280; border-bottom: 1.5px solid #e5e7eb; padding: 4px 8px; }
        th:first-child { text-align: left; }
        td { padding: 4px 8px; border-bottom: 1px solid #f0f1f3; }
        td.num { text-align: right; }
        .insight { margin: 6px 0; padding: 6px 10px; background: #fafafa; }
        .insight-head { font-weight: 700; margin-bottom: 2px; }
        .tag { font-weight: 400; font-size: 11px; color: #6b7280; float: right; }
        .muted { color: #6b7280; }
        .foot { margin-top: 24px; font-size: 10px; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 6px; }
        @media print { body { margin: 12mm; } }
      </style></head><body>
      <h1>Staff annual review — ${esc(displayName(review.staffName, masked))}</h1>
      <div class="sub">${esc(review.roleName || "—")} · ${esc(displayName(pharmacyName, masked))} · Cycle ${esc(review.reviewYear)}
        · window ${esc(fmtDate(review.windowStart))} → ${esc(fmtDate(review.windowEnd))}
        · computed ${esc(fmtDate(review.computedAt))}</div>

      <h2>Narrative summary</h2>
      <div class="narrative">${m(review.narrative)}</div>

      <h2>Insights (${(review.insights || []).length})</h2>
      ${insightsHtml}

      <h2>Near misses — cohort: ${review.cohort?.type === "role"
        ? esc(`${review.cohort.roleName || "role-matched"} (${review.cohort.size})`)
        : esc(`whole pharmacy (${review.cohort?.size ?? "—"})`)}</h2>
      <table>
        <tr><th>Metric</th><th>Count</th><th>Per shift</th><th>Cohort avg</th><th>Ratio</th></tr>
        ${metricRows([
          { label: "As dispenser (errors attributed)", block: review.nearMisses?.asDispenser },
          { label: "As checker (errors caught)", block: review.nearMisses?.asChecker },
          review.nearMisses?.asReporter ? { label: "As reporter", block: review.nearMisses.asReporter } : null
        ])}
      </table>
      ${cats.length ? `<p>Error types: ${esc(cats.map(c => `${c.label} (${c.count})`).join(", "))}</p>` : ""}
      ${review.nearMissAnalysis && !review.nearMissAnalysis.error && review.nearMissAnalysis.total > 0 ? `
        <p><b>Pattern analysis</b> (${esc(review.nearMissAnalysis.total)} incidents${review.nearMissAnalysis.shareOfPharmacy !== null ? `, ${esc(review.nearMissAnalysis.shareOfPharmacy)}% of branch` : ""}):
          monthly spread ${esc(Object.entries(review.nearMissAnalysis.monthly || {}).map(([k, v]) => `${k.slice(5)}: ${v}`).join(", "))}.
          ${(review.nearMissAnalysis.dayOfWeek || []).filter(d => d.count > 0).length ? `Days: ${esc(review.nearMissAnalysis.dayOfWeek.filter(d => d.count > 0).map(d => `${d.day} ${d.count}`).join(", "))}.` : ""}
          ${(review.nearMissAnalysis.causes || []).length ? `Recorded causes: ${esc(review.nearMissAnalysis.causes.map(c => `${c.family} (${c.count})`).join(", "))}.` : ""}
          Pattern findings appear as SN-series insights above (shared rule set with the branch monthly engine).</p>` : ""}

      <h2>Patient medication incidents</h2>
      <p>Involved in <b>${esc(review.medicationIncidents?.involvedCount ?? 0)}</b>
        (${esc(review.medicationIncidents?.asDispenser ?? 0)} as dispenser,
        ${esc(review.medicationIncidents?.asChecker ?? 0)} as checker)
        · cohort average ${esc(fmtNum(review.medicationIncidents?.cohortAvg))}</p>

      <h2>Attendance</h2>
      ${att.hasData
        ? `<p><b>${esc(att.shiftsWorked)}</b> shifts worked ·
           <b>${esc(att.lateCount)}</b> late starts (${esc(fmtPct(att.lateRate))}${att.cohortLateRate !== null ? `, cohort ${esc(fmtPct(att.cohortLateRate))}` : ""}${att.lateCount > 0 ? `, avg ${esc(att.avgLatenessMins)} min` : ""}) ·
           <b>${esc(att.earlyCount)}</b> early departures (${esc(fmtPct(att.earlyRate))}${att.cohortEarlyRate !== null ? `, cohort ${esc(fmtPct(att.cohortEarlyRate))}` : ""}) ·
           <b>${esc(att.narrativeNotes)}</b> shift notes</p>
           <p class="muted">Heuristic: ${esc(att.heuristic)}</p>`
        : `<p class="muted">No attendance records matched this staff member in the window.</p>`}

      ${(review.activitySources || []).length ? `
        <h2>Engagement (activity sources)</h2>
        <table>
          <tr><th>Source</th><th>Count</th><th>Per shift</th><th>Cohort avg</th><th>Ratio</th></tr>
          ${metricRows(review.activitySources.map(src => ({ label: src.label, block: src })))}
        </table>` : ""}

      ${review.sops && review.sops.totalCards > 0 ? `
        <h2>SOP reading cards</h2>
        <p><b>${esc(review.sops.read)}</b> of <b>${esc(review.sops.totalCards)}</b> read and signed
          (${esc(fmtPct(review.sops.readPct))}${review.sops.cohortReadPct !== null ? `, cohort ${esc(fmtPct(review.sops.cohortReadPct))}` : ""})
          · <b>${esc(review.sops.remindedInWindow)}</b> card${review.sops.remindedInWindow === 1 ? "" : "s"} needed a reminder this year</p>
        ${(review.sops.unreadCards || []).length ? `
          <p><b>Unread (${esc(review.sops.unread)}), oldest first:</b></p>
          <table>
            <tr><th style="text-align:left">SOP</th><th>Assigned</th><th>Due</th><th>Last reminded</th></tr>
            ${review.sops.unreadCards.map(c => `
              <tr>
                <td>${esc(c.title)}</td>
                <td class="num">${esc(fmtDate(c.assignedAt))}</td>
                <td class="num">${esc(fmtDate(c.dueDate))}</td>
                <td class="num">${esc(fmtDate(c.lastReminderSentAt))}</td>
              </tr>`).join("")}
          </table>` : ""}
        <p class="muted">${esc(review.sops.note)}</p>` : ""}

      <h2>Year-on-year</h2>
      ${review.comparison?.hasPriorYear
        ? `<p>vs ${esc(review.comparison.priorReviewYear)}: near misses as dispenser
           ${esc(review.comparison.priorNearMissesAsDispenser)} → ${esc(review.nearMisses?.asDispenser?.value)}
           ${review.comparison.lateRateDelta !== null ? ` · late-start rate delta ${review.comparison.lateRateDelta > 0 ? "+" : ""}${esc(fmtPct(review.comparison.lateRateDelta))}` : ""}</p>`
        : `<p class="muted">No previous review on record — baseline year.</p>`}

      <div class="foot">Discussion aid assembled from operational records — not a performance verdict.
        Attendance timings use the :00/:30 boundary heuristic and should be verified against agreed hours.
        Generated ${esc(new Date().toLocaleString("en-GB"))} · Pharmace Digital staff review engine.</div>
      </body></html>`;

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => { try { document.body.removeChild(iframe); } catch (_) {} }, 2000);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 8, width: "min(940px, 94vw)",
          maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden",
          boxShadow: "0 10px 40px rgba(0,0,0,0.25)"
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "14px 18px", borderBottom: "1px solid #e5e7eb", gap: 12
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>{nameShown}</span>
              <span style={{
                background: isCurrentCycle ? "#e8f1fd" : "#fef3c7",
                border: `1px solid ${isCurrentCycle ? "#93c5fd" : "#fcd34d"}`,
                color: isCurrentCycle ? "#1976d2" : "#92400e",
                borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap"
              }}>
                Viewing cycle {popupYear}{isCurrentCycle ? " (current)" : ""}
              </span>
              <label style={{ fontSize: 12, color: "#6b7280", display: "inline-flex", alignItems: "center", gap: 6 }}>
                Cycle year:
                <select
                  value={popupYear}
                  onChange={e => setPopupYear(Number(e.target.value))}
                  style={{ fontSize: 12, padding: "2px 6px", border: "1px solid #9ca3af", borderRadius: 4, background: "#fff", cursor: "pointer" }}
                >
                  {yearOptions().map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </label>
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              {(review && review.roleName) || "—"} · {pharmacyName}
              {review && <> · window {fmtDate(review.windowStart)} → {fmtDate(review.windowEnd)} · computed {fmtDate(review.computedAt)}</>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <button
              onClick={printReview}
              disabled={!review}
              title="Print this review as a formatted document for the meeting."
              style={{
                border: "1px solid #059669", color: "#059669", background: "#fff",
                borderRadius: 4, padding: "6px 12px", fontSize: 13, fontWeight: 600,
                cursor: review ? "pointer" : "not-allowed", opacity: review ? 1 : 0.5
              }}
            >
              🖨 Print
            </button>
            <button
              onClick={onClose}
              title="Close (Esc)"
              style={{
                border: "1px solid #d1d5db", color: "#374151", background: "#fff",
                borderRadius: 4, padding: "6px 12px", fontSize: 13, fontWeight: 600,
                cursor: "pointer"
              }}
            >
              ✕ Close
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 18 }}>
          {loading ? (
            <div style={{ color: "#6b7280", fontSize: 14 }}>Computing review for cycle {popupYear}… (rebuilding the 12-month dataset)</div>
          ) : error ? (
            <div style={{ color: "#dc2626", fontSize: 14 }}>Error: {error}</div>
          ) : !review ? (
            <div style={{ fontSize: 14, color: "#6b7280" }}>
              No review could be computed for this staff member and cycle.
            </div>
          ) : (
            <ReviewPackView review={review} staffName={staffName} masked={masked} />
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Main grid ---------------- */

export default function Staff() {
  const masked = useMaskNames();

  const [pharmacies, setPharmacies] = useState([]);
  // v7: the fleet list now has to finish loading before the grid can do
  // anything, because "All pharmacies" is the DEFAULT selection and its
  // fan-out iterates that list. Tracking loaded/error separately means a
  // failed fleet fetch reports itself instead of leaving the grid on
  // "Loading staff…" forever.
  const [pharmaciesLoaded, setPharmaciesLoaded] = useState(false);
  const [pharmaciesErr, setPharmaciesErr] = useState(null);
  // v5: "All pharmacies" mode — fans the review + grid-info fetches out
  // across every pharmacy in scope and merges, with a Pharmacy column and
  // per-row pharmacy attribution so every action (book, pre-appraisal,
  // appraisal, snapshot) still hits the right tenant.
  const ALL = "__ALL__";
  // v7: opens on ALL rather than "Select a pharmacy…" — the page is used
  // far more often to sweep the whole fleet for who is overdue than to
  // look at one branch, and the old default made that the slower path.
  const [pharmacyFilter, setPharmacyFilter] = useState(ALL);
  const isAll = pharmacyFilter === ALL;
  const gridYear = new Date().getUTCFullYear(); // grid lists the current cycle; year switching lives inside the popup
  const [roleFilter, setRoleFilter] = useState("");
  const [search, setSearch] = useState("");
  const [manageOpen, setManageOpen] = useState(false);

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [popup, setPopup] = useState(null); // { staffId, staffName }
  const [appraisal, setAppraisal] = useState(null); // { staffId, staffName, roleName }
  const [preSending, setPreSending] = useState(null); // staffId currently being emailed
  const [gridInfo, setGridInfo] = useState({});       // v4: staffId -> { createdAt, lastAppraisalDate, nextBooked, staffName, roleName }
  const [booking, setBooking] = useState(null);       // v4: { staffId, staffName } — Book appraisal dialog
  const [bookingDate, setBookingDate] = useState("");
  const [bookingBusy, setBookingBusy] = useState(false);

  // v4: record-started / last-appraisal / next-booked per staff member.
  // v5: gridInfo is keyed "<pharmacy>|<staffId>" in BOTH modes so the
  // all-pharmacies merge can never collide across tenants.
  const gKey = (ph, staffId) => `${ph}|${String(staffId)}`;
  const rowPh = (r) => r.__ph || pharmacyFilter;
  const loadGridInfo = React.useCallback(async () => {
    if (!pharmacyFilter) { setGridInfo({}); return; }
    // v7: same wait as the reviews effect — ALL fans out over the fleet
    // list, which isn't there yet on the first render.
    if (isAll && !pharmaciesLoaded) return;
    const fetchOne = async (phValue) => {
      const res = await fetch(`${API}/api/appraisals/grid-info`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ pharmacyName: phValue })
      });
      const d = await res.json();
      if (!res.ok) return {};
      const out = {};
      for (const [sid, g] of Object.entries(d.info || {})) out[gKey(phValue, sid)] = { ...g, __ph: phValue };
      return out;
    };
    try {
      if (isAll) {
        const targets = pharmacies.map(p => p.lookupName || p.pharmacyName);
        const merged = {};
        let i = 0;
        const worker = async () => {
          while (i < targets.length) {
            const t = targets[i++];
            try { Object.assign(merged, await fetchOne(t)); } catch (_) {}
          }
        };
        await Promise.all(Array.from({ length: Math.min(4, targets.length) }, worker));
        setGridInfo(merged);
      } else {
        setGridInfo(await fetchOne(pharmacyFilter));
      }
    } catch (_) {}
  }, [pharmacyFilter, isAll, pharmacies, pharmaciesLoaded]);
  useEffect(() => { loadGridInfo(); }, [loadGridInfo]);

  // v4: confirm a booking — creates the appraisal skeleton for that year,
  // emails the meeting invitation AND the pre-appraisal questionnaire, and
  // logs both emails on the skeleton (Emails section of the dialogue).
  const confirmBooking = async () => {
    if (!bookingDate) { window.alert("Pick a date first."); return; }
    setBookingBusy(true);
    try {
      const res = await fetch(`${API}/api/appraisals/book`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ pharmacyName: booking.ph || pharmacyFilter, staffId: booking.staffId, meetingDate: bookingDate })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      window.alert(`Appraisal booked for ${bookingDate} — invitation emailed to ${d.sentTo}${d.preAppraisalSent ? ", pre-appraisal questionnaire sent alongside it" : d.preAppraisalError ? ` (pre-appraisal could not be sent: ${d.preAppraisalError})` : ""}. Both emails are logged on the appraisal record.`);
      setBooking(null); setBookingDate("");
      loadGridInfo();
    } catch (e) { window.alert(`Could not book: ${e.message}`); }
    finally { setBookingBusy(false); }
  };

  // v3: email the pre-appraisal questionnaire (tokenised dynamic form) to
  // one staff member. Their answers autosave into staff_preappraisals and
  // appear read-only on the appraisal dialogue's Pre-appraisal tab.
  const sendPreAppraisal = async (r) => {
    const year = new Date().getUTCFullYear();
    if (!window.confirm(`Email the ${year} pre-appraisal questionnaire to ${r.staffName}?\n\nIt outlines the purpose and structure of the appraisal, asks a set of reflection questions, and has them self-rate on benchmarks like punctuality, confidentiality and accuracy. Their answers save automatically as they type.`)) return;
    setPreSending(String(r.staffId));
    try {
      const res = await fetch(`${API}/api/appraisals/pre/send`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ pharmacyName: rowPh(r), staffId: String(r.staffId), appraisalYear: year })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      window.alert(`Pre-appraisal ${d.resend ? "re-sent" : "sent"} to ${d.sentTo}.`);
    } catch (e) { window.alert(`Could not send: ${e.message}`); }
    finally { setPreSending(null); }
  };
  const [apprStatus, setApprStatus] = useState(null); // fleet appraisal status
  const [apprOpen, setApprOpen] = useState(false);   // status drill expanded

  // Fleet appraisal status (amber strip) — one call on page load.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/appraisals/status`, { method: "POST", headers: authHeaders(), body: "{}" });
        const d = await res.json();
        if (res.ok) setApprStatus(d);
      } catch (_) {}
    })();
  }, []);

  // Pharmacy list — same JWT-scoped fleet endpoint SinglePharmacy.js uses.
  // Returns [{ pharmacyId, pharmacyName, lookupName, resolvable }] where
  // lookupName is the raw pharmacies.name the review endpoints resolve on.
  // NOTE: /api/pharmacies is a typeahead SEARCH endpoint (needs ?q=) and
  // returns [] without it — do not use it for the full list.
  useEffect(() => {
    (async () => {
      try {
        const org = localStorage.getItem("organisationName") || "";
        const qs = org ? `?organisationName=${encodeURIComponent(org)}` : "";
        const res = await fetch(`${API}/api/headoffice/single/pharmacies${qs}`, {
          headers: authHeaders()
        });
        const data = await res.json();
        const list = (Array.isArray(data) ? data : [])
          .filter(p => p && (p.lookupName || p.pharmacyName))
          .sort((a, b) => (a.pharmacyName || "").localeCompare(b.pharmacyName || ""));
        setPharmacies(list);
        setPharmaciesErr(null);
      } catch (e) {
        console.error("Error fetching pharmacies:", e);
        setPharmaciesErr(e.message || "Could not load the pharmacy list.");
      } finally {
        // Always flips, success or failure — the grid effects below wait on
        // this, so leaving it false on error would hang them permanently.
        setPharmaciesLoaded(true);
      }
    })();
  }, []);

  // Review summaries for the selected pharmacy + cycle. In "All pharmacies"
  // mode the same endpoint is called once per pharmacy (small concurrency
  // pool) and results are merged, each row tagged with its pharmacy.
  useEffect(() => {
    if (!pharmacyFilter) { setReviews([]); return; }
    // v7: ALL is the default now, so this effect fires on first paint —
    // before the fleet list has arrived. Without this the fan-out would run
    // against an empty target list and flash "No staff found" before the
    // real rows land a moment later. Hold the loading state instead.
    if (isAll && !pharmaciesLoaded) { setLoading(true); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const fetchOne = async (phValue, phDisplay) => {
          const res = await fetch(`${API}/staff_annual_reviews`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({ pharmacyName: phValue, reviewYear: gridYear })
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
          return (data.reviews || []).map(r => ({ ...r, __ph: phValue, __phDisplay: phDisplay }));
        };
        if (isAll) {
          const targets = pharmacies.map(p => ({ value: p.lookupName || p.pharmacyName, display: p.pharmacyName || p.lookupName }));
          const merged = [];
          let i = 0;
          const worker = async () => {
            while (i < targets.length && !cancelled) {
              const t = targets[i++];
              try { merged.push(...await fetchOne(t.value, t.display)); }
              catch (e) { console.error(`[reviews] ${t.display}:`, e.message); }
            }
          };
          await Promise.all(Array.from({ length: Math.min(4, targets.length) }, worker));
          if (!cancelled) setReviews(merged);
        } else {
          const rows = await fetchOne(pharmacyFilter, pharmacyFilter);
          if (!cancelled) setReviews(rows);
        }
      } catch (e) {
        if (!cancelled) { setError(e.message); setReviews([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pharmacyFilter, gridYear, isAll, pharmacies, pharmaciesLoaded]);

  const roles = useMemo(
    () => [...new Set(reviews.map(r => r.roleName).filter(Boolean))].sort(),
    [reviews]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    // v4: staff with no computed review yet (new starters) still appear —
    // merged in from grid-info so booking and appraisals work for them.
    // v5: gridInfo keys are "<pharmacy>|<staffId>".
    const seen = new Set(reviews.map(r => gKey(rowPh(r), r.staffId)));
    const extras = Object.entries(gridInfo)
      .filter(([key, g]) => !seen.has(key) && g.staffName)
      .map(([key, g]) => {
        const sep = key.indexOf("|");
        const ph = key.slice(0, sep), id = key.slice(sep + 1);
        const disp = (pharmacies.find(p => (p.lookupName || p.pharmacyName) === ph) || {}).pharmacyName || ph;
        return { staffId: id, staffName: g.staffName, roleName: g.roleName || "—", __ph: ph, __phDisplay: disp };
      });
    return [...reviews, ...extras]
      .filter(r => {
        if (roleFilter && r.roleName !== roleFilter) return false;
        if (q) {
          const haystack = [r.staffName, r.roleName].filter(Boolean).join(" ").toLowerCase();
          if (!haystack.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => isAll
        ? ((a.__phDisplay || "").localeCompare(b.__phDisplay || "") || (a.staffName || "").localeCompare(b.staffName || ""))
        : (a.staffName || "").localeCompare(b.staffName || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviews, gridInfo, roleFilter, search, isAll, pharmacies, pharmacyFilter]);

  const totals = useMemo(() => ({
    staff: filtered.length,
    positives: filtered.reduce((s, r) => s + (r.positives || 0), 0),
    concerns: filtered.reduce((s, r) => s + (r.concerns || 0), 0)
  }), [filtered]);

  const th = {
    textAlign: "left", padding: "10px 12px", fontSize: 11, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: 0.4, color: "#6b7280",
    borderBottom: "2px solid #e5e7eb", whiteSpace: "nowrap"
  };
  const td = { padding: "10px 12px", fontSize: 13, borderBottom: "1px solid #f0f1f3", verticalAlign: "top" };
  const selStyle = {
    fontSize: 13, padding: "6px 10px", border: "1px solid #9ca3af",
    borderRadius: 4, background: "#fff", cursor: "pointer", minWidth: 180
  };

  // v4: dd/mm/yy for the grid's date columns
  const fmtDMY = (d) => {
    if (!d) return "—";
    const x = new Date(d);
    if (isNaN(x)) return "—";
    return `${String(x.getDate()).padStart(2, "0")}/${String(x.getMonth() + 1).padStart(2, "0")}/${String(x.getFullYear()).slice(2)}`;
  };

  // v4: pale red when an appraisal is DUE — over a year since the last
  // appraisal (or, if none, since the record started), OR a booked meeting
  // date has passed without the appraisal being completed.
  const appraisalDue = (r) => {
    const g = gridInfo[gKey(rowPh(r), r.staffId)];
    if (!g) return false;
    const now = Date.now(), YEAR = 365 * 24 * 3600 * 1000;
    const anchor = g.lastAppraisalDate || g.createdAt;
    if (anchor && now - new Date(anchor).getTime() > YEAR) return true;
    if (g.nextBooked?.date && new Date(g.nextBooked.date + "T23:59:59").getTime() < now) return true;
    return false;
  };
  // v5: RAG row background.
  //   green — appraised within the last 12 months
  //   amber — not appraised in the last 12 months, but a future date is booked
  //   red   — never appraised or over a year ago with nothing booked ahead,
  //           or a booked date has passed without the appraisal being done
  const appraisalState = (r) => {
    const g = gridInfo[gKey(rowPh(r), r.staffId)];
    if (!g) return "none";
    const now = Date.now(), YEAR = 365 * 24 * 3600 * 1000;

    const last = g.lastAppraisalDate ? new Date(g.lastAppraisalDate).getTime() : NaN;
    if (!isNaN(last) && now - last <= YEAR) return "green";

    const booked = g.nextBooked?.date
      ? new Date(g.nextBooked.date + "T23:59:59").getTime()
      : NaN;
    if (!isNaN(booked) && booked >= now) return "amber";

    return "red";
  };
  const rowBg = (r) => ({
    green: "#dcfce7",
    amber: "#fef3c7",
    red: "#fee2e2",
    none: "transparent",
  }[appraisalState(r)]);

  return (
    <div style={{ padding: "16px 20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>Staff annual reviews</h2>
          <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
            12-month evidence packs computed by the staff review engine — near misses,
            medication incidents, attendance and engagement, each vs the branch cohort.
            Click a staff member for the full review.
          </div>
        </div>
        <MaskToggleButton />
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
        <label style={{ display: "inline-flex", flexDirection: "column", gap: 4, fontSize: 11, color: "#6b7280" }}>
          PHARMACY
          <select value={pharmacyFilter} onChange={e => setPharmacyFilter(e.target.value)} style={selStyle}>
            <option value={ALL}>All pharmacies</option>
            <option value="">Select a pharmacy…</option>
            {pharmacies.map(p => {
              const value = p.lookupName || p.pharmacyName;
              return (
                <option key={value} value={value}>
                  {displayName(p.pharmacyName || p.lookupName, masked)}
                </option>
              );
            })}
          </select>
        </label>
        <label style={{ display: "inline-flex", flexDirection: "column", gap: 4, fontSize: 11, color: "#6b7280" }}>
          ROLE
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={selStyle}>
            <option value="">All roles</option>
            {roles.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <label style={{ display: "inline-flex", flexDirection: "column", gap: 4, fontSize: 11, color: "#6b7280" }}>
          SEARCH
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Name / role…"
            style={{ fontSize: 13, padding: "6px 8px", border: "1px solid #9ca3af", borderRadius: 4, width: 150 }}
          />
        </label>
        <button
          onClick={() => setManageOpen(true)}
          title="Add or edit staff, change roles, and show a sign-in QR code."
          style={{ marginLeft: "auto", alignSelf: "flex-end", background: "#177245",
                   color: "#fff", border: 0, borderRadius: 4, padding: "7px 14px",
                   fontSize: 13, cursor: "pointer" }}>
          Manage staff
        </button>
      </div>

      {manageOpen && <ManageStaff onClose={() => setManageOpen(false)} />}

      {/* Head-office appraisal status */}
      {apprStatus && (
        <div style={{
          border: `1px solid ${apprStatus.overall === "amber" ? "#fcd34d" : "#86efac"}`,
          background: apprStatus.overall === "amber" ? "#fffbeb" : "#f0fdf4",
          borderRadius: 8, padding: "10px 14px", margin: "0 0 14px"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: apprStatus.overall === "amber" ? "#92400e" : "#166534" }}>
              {apprStatus.overall === "amber"
                ? `⚠ Appraisals outstanding — ${apprStatus.outstanding.reduce((a, o) => a + o.overdue, 0)} overdue, ${apprStatus.outstanding.reduce((a, o) => a + o.due, 0)} due across ${apprStatus.outstanding.length} pharmac${apprStatus.outstanding.length === 1 ? "y" : "ies"}`
                : "✓ Appraisals up to date across the fleet"}
            </div>
            <button onClick={() => setApprOpen(o => !o)}
              style={{ border: "1px solid #d1d5db", background: "#fff", borderRadius: 4, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>
              {apprOpen ? "Hide detail" : "Show detail"}
            </button>
          </div>
          {apprOpen && (
            <div style={{ marginTop: 10 }}>
              {apprStatus.outstanding.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 4 }}>
                    Needs appraisal
                  </div>
                  {apprStatus.outstanding.map((o, i) => (
                    <div key={i} style={{ fontSize: 13, color: "#1f2937", padding: "2px 0" }}>
                      <b>{displayName(o.pharmacyName, masked)}</b> — {o.overdue} overdue{o.due ? `, ${o.due} due` : ""}
                      {o.overdueNames.length > 0 && (
                        <span style={{ color: "#6b7280" }}> ({o.overdueNames.map(n => displayName(n, masked)).join(", ")})</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {apprStatus.perPharmacy.map((p, i) => {
                  const ph = pharmacies.find(x => x.pharmacyName === p.pharmacyName || x.lookupName === p.pharmacyName);
                  return (
                    <span key={i}
                      onClick={() => ph && setPharmacyFilter(ph.lookupName || ph.pharmacyName)}
                      title={`${p.ok} ok · ${p.due} due · ${p.overdue} overdue — click to open this pharmacy`}
                      style={{
                        fontSize: 11, padding: "3px 10px", borderRadius: 999, cursor: ph ? "pointer" : "default",
                        background: p.overdue > 0 ? "#fee2e2" : p.due > 0 ? "#fef3c7" : "#dcfce7",
                        border: `1px solid ${p.overdue > 0 ? "#fca5a5" : p.due > 0 ? "#fcd34d" : "#86efac"}`,
                        color: p.overdue > 0 ? "#991b1b" : p.due > 0 ? "#92400e" : "#166534", fontWeight: 600
                      }}>
                      {displayName(p.pharmacyName, masked)} {p.overdue > 0 ? `· ${p.overdue}⚠` : p.due > 0 ? `· ${p.due} due` : "✓"}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Grid */}
      <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ background: "#f9fafb" }}>
            <tr>
              {isAll && <th style={th}>Pharmacy</th>}
              <th style={th}>Staff name</th>
              <th style={th}>Performance</th>
              <th style={th}>Role</th>
              <th style={th}>Record started</th>
              <th style={th}>Last appraisal</th>
              <th style={th}>Next appraisal booked for</th>
              <th style={th}>Pre-appraisal / Appraisal</th>
            </tr>
          </thead>
          <tbody>
            {!pharmacyFilter ? (
              <tr><td colSpan={isAll ? 8 : 7} style={{ ...td, textAlign: "center", color: "#6b7280", padding: 24 }}>
                Select a pharmacy to load its staff.
              </td></tr>
            ) : loading ? (
              <tr><td colSpan={isAll ? 8 : 7} style={{ ...td, textAlign: "center", color: "#6b7280", padding: 24 }}>
                Loading staff…
              </td></tr>
            ) : error ? (
              <tr><td colSpan={isAll ? 8 : 7} style={{ ...td, textAlign: "center", color: "#dc2626", padding: 24 }}>
                Error: {error}
              </td></tr>
            ) : isAll && pharmaciesErr ? (
              /* v7: with ALL as the default, a failed fleet fetch used to
                 look like "no staff" — say what actually went wrong. */
              <tr><td colSpan={8} style={{ ...td, textAlign: "center", color: "#dc2626", padding: 24 }}>
                Could not load the pharmacy list, so "All pharmacies" has nothing to sweep: {pharmaciesErr}
              </td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={isAll ? 8 : 7} style={{ ...td, textAlign: "center", color: "#6b7280", padding: 24 }}>
                {isAll ? "No staff found across the fleet." : "No staff found for this pharmacy."}
              </td></tr>
            ) : (
              filtered.map(r => {
                const g = gridInfo[gKey(rowPh(r), r.staffId)] || {};
                return (
                <tr key={gKey(rowPh(r), r.staffId)} style={{ background: rowBg(r) }}>
                  {isAll && (
                    <td style={{ ...td, whiteSpace: "nowrap", fontWeight: 600, color: "#374151" }}>
                      {displayName(r.__phDisplay || rowPh(r), masked)}
                    </td>
                  )}
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    <span style={{ fontWeight: 600 }}>{displayName(r.staffName, masked) || "(unnamed)"}</span>
                  </td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    <button
                      onClick={() => setPopup({ staffId: String(r.staffId), staffName: r.staffName, ph: rowPh(r) })}
                      style={{ border: "1px solid #4338ca", color: "#4338ca", background: "#fff", borderRadius: 4, padding: "3px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                      title="Computes and opens the full 12-month performance analysis for this staff member.">
                      Performance snapshot
                    </button>
                  </td>
                  <td style={td}>{r.roleName || "—"}</td>
                  <td style={td} title="Date this staff record was created">{fmtDMY(g.createdAt)}</td>
                  <td style={td}
                      title={g.lastAppraisalSource === "simple"
                        ? "Date of the last record entered with the SIMPLE appraisal tool. There is no evidence pack, pre-appraisal or sign-off behind this date."
                        : "Meeting date of the last COMPLETED full appraisal"}>
                    {g.lastAppraisalSource === "simple" && g.lastAppraisalDate
                      ? <span style={SIMPLE_PILL}>{fmtDMY(g.lastAppraisalDate)}</span>
                      : fmtDMY(g.lastAppraisalDate)}
                  </td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    {g.nextBooked?.date && g.nextBookedSource === "simple" ? (
                      // A simple record's nextReviewDate is a DUE date, not a
                      // booked meeting — say so rather than implying an
                      // invitation has gone out, because none has.
                      <span title="Next review DUE date carried on the last simple appraisal record. Nothing is booked and no invitation has been sent — use Book appraisal to arrange the meeting."
                        style={SIMPLE_PILL}>
                        {fmtDMY(g.nextBooked.date)} <span style={{ fontWeight: 600, opacity: 0.8 }}>due</span>
                      </span>
                    ) : g.nextBooked?.date ? (
                      <span title={`Booked (${g.nextBooked.appraisalYear} appraisal, not yet completed)`}
                        style={{ fontWeight: 700, color: appraisalDue(r) && new Date(g.nextBooked.date) < new Date() ? "#991b1b" : "#1976d2" }}>
                        {fmtDMY(g.nextBooked.date)}
                      </span>
                    ) : (
                      <button
                        onClick={() => { setBooking({ staffId: String(r.staffId), staffName: r.staffName, ph: rowPh(r) }); setBookingDate(""); }}
                        style={{ border: "1px solid #1976d2", color: "#1976d2", background: "#fff", borderRadius: 4, padding: "3px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                        title="Pick a meeting date — confirming emails the invitation AND the pre-appraisal questionnaire, creates the appraisal skeleton, and logs both emails on it.">
                        Book appraisal
                      </button>
                    )}
                  </td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    <button
                      onClick={() => sendPreAppraisal(r)}
                      disabled={preSending === String(r.staffId)}
                      style={{ border: "1px solid #4338ca", color: "#4338ca", background: "#fff", borderRadius: 4, padding: "3px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", marginRight: 6 }}
                      title="Email this staff member the pre-appraisal questionnaire — purpose & structure of the appraisal, reflection questions, and 1–5 self-ratings on community-pharmacy benchmarks. Answers save automatically as they type and show on the appraisal's Pre-appraisal tab.">
                      {preSending === String(r.staffId) ? "Sending…" : "Send pre-appraisal"}
                    </button>
                    <button
                      onClick={() => setAppraisal({ staffId: String(r.staffId), staffName: r.staffName, roleName: r.roleName, ph: rowPh(r) })}
                      style={{ border: "1px solid #1976d2", color: "#1976d2", background: "#fff", borderRadius: 4, padding: "3px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                      title="Open this staff member's appraisal record (year tabs; complete and send summary)">
                      Appraisal
                    </button>
                  </td>
                </tr>
              ); })
            )}
          </tbody>
          <tfoot>
            <tr style={{ background: "#f3f4f6", fontWeight: 700 }}>
              <td style={{ ...td, borderBottom: "none" }} colSpan={isAll ? 8 : 7}>
                Staff: {totals.staff}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 18, marginTop: 12, fontSize: 12, color: "#374151", flexWrap: "wrap" }}>
        {[
          { bg: "#dcfce7", br: "#86efac", label: "Appraised within the last 12 months" },
          { bg: "#fef3c7", br: "#fcd34d", label: "Appraisal booked, not yet done" },
          { bg: "#fee2e2", br: "#fca5a5", label: "Never appraised, over a year ago, or a booked date has passed" },
          { bg: "#eff6ff", br: "#93c5fd", label: "Teal date — from the simple appraisal tool, not a full appraisal" },
        ].map(k => (
          <span key={k.label}>
            <span style={{ display: "inline-block", width: 12, height: 12, background: k.bg, border: `1px solid ${k.br}`, borderRadius: 2, verticalAlign: "middle", marginRight: 6 }} />
            {k.label}
          </span>
        ))}
      </div>

      {/* v4: Book appraisal dialog */}
      {booking && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200 }}>
          <div style={{ background: "#fff", borderRadius: 8, width: "min(460px, 92vw)", padding: 18 }}>
            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 6 }}>Book appraisal — {displayName(booking.staffName, masked)}</div>
            <div style={{ fontSize: 13, color: "#374151", marginBottom: 12, lineHeight: 1.45 }}>
              Confirming will: create the appraisal record for the chosen year, email {displayName(booking.staffName, masked)} the
              meeting invitation (purpose &amp; structure), send the <b>pre-appraisal questionnaire</b> at the same time,
              and log both emails on the appraisal record (Emails section).
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 4 }}>Meeting date</div>
            <input type="date" value={bookingDate} min={new Date().toISOString().slice(0, 10)}
              onChange={e => setBookingDate(e.target.value)}
              style={{ fontSize: 14, padding: "7px 10px", border: "1px solid #9ca3af", borderRadius: 4, width: 200 }} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
              <button onClick={() => { setBooking(null); setBookingDate(""); }} disabled={bookingBusy}
                style={{ border: "1px solid #6b7280", color: "#6b7280", background: "#fff", borderRadius: 4, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={confirmBooking} disabled={bookingBusy || !bookingDate}
                style={{ border: "none", background: "#1976d2", color: "#fff", borderRadius: 4, padding: "7px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: bookingDate ? 1 : 0.5 }}>
                {bookingBusy ? "Booking…" : "Confirm & send emails"}
              </button>
            </div>
          </div>
        </div>
      )}

      {appraisal && (
        <StaffAppraisal
          pharmacyName={appraisal.ph || pharmacyFilter}
          staffId={appraisal.staffId}
          staffName={appraisal.staffName}
          roleName={appraisal.roleName}
          onClose={() => { setAppraisal(null); loadGridInfo(); }}
        />
      )}

      {popup && (
        <ReviewPopup
          pharmacyName={popup.ph || pharmacyFilter}
          staffId={popup.staffId}
          staffName={popup.staffName}
          reviewYear={gridYear}
          masked={masked}
          onClose={() => setPopup(null)}
        />
      )}
    </div>
  );
}